<template>
  <div>
    <h1>Subskrypcje emailowe</h1>

    <v-row slot="actions-prepend">
      <v-col md="4" cols="12">
        <v-text-field label="Szukaj" single-line v-model="search" />
      </v-col>
    </v-row>

    <v-data-table
      :custom-filter="filterOverAllProperty"
      :headers="headers"
      :items="notificationResponse"
      :loading="$store.getters['Ajax/loading'] ? 'accent' : false"
      :search="search"
      :expanded.sync="expanded"
      single-expand
    >
      <template #item="props">
        <tr>
          <td class="text-left">
            <h2>{{ props.item.name }}</h2>
          </td>
          <td class="text-left">
            <table>
              <tr :key="notificationSubscription.id" v-for="notificationSubscription of props.item.notificationSubscriptions">
                <td>
                  {{ notificationEventTypes.find(v => v.value === notificationSubscription.type).text }}
                  <template v-if="!!notificationSubscription.notify_after">
                    <br />
                    Powiadom po: {{ notificationSubscription.notify_after.replace("+", "") }}
                  </template>
                  <template v-if="!!notificationSubscription.repeat_notify_after">
                    <br />
                    Powtarzaj po: {{ notificationSubscription.repeat_notify_after.replace("+", "") }}
                  </template>
                </td>
                <td>{{ notificationSubscription.email }}</td>
                <td>{{ notificationSubscription.subject_mask }}</td>
                <td>
                  <v-btn :disabled="$store.getters['Ajax/loading']" @click="remove(notificationSubscription.id)" icon>
                    <v-icon>delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </table>

            <v-chip color="red" outlined v-if="props.item.notificationSubscriptions.length === 0">Brak notyfikacji mailowych</v-chip>
          </td>
          <td>
            <v-btn
              v-if="selectedCompany === props.item.id"
              @click="
                expanded.splice(expanded.indexOf(props.item), 1);
                selectedCompany = null;
              "
              icon
            >
              <v-icon>expand_less</v-icon>
            </v-btn>
            <v-btn v-else @click="openEditRow(props)" icon>
              <v-icon>expand_more</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
      <template #expanded-item="props">
        <td colspan="100%">
          <v-form lazy ref="form">
            <v-row class="px-3">
              <div class="px-3" style="padding-top: 20px">
                <span><v-icon>fa-arrow-up</v-icon></span>
              </div>
              <v-autocomplete
                :items="notificationEventTypes"
                :rules="[v => v != null || $t('global.error.required')]"
                label="Typ zdarzenia*"
                v-model="selectedType"
              />
              <v-text-field :rules="emailRules" class="mx-3" label="Email*" v-model="selectedEmail" />
              <br />
              <v-text-field
                @click:append-outer="openDialogInfo(props.item.id)"
                append-outer-icon="fa-info-circle"
                class="mx-3"
                label="Maska tematu"
                v-model="selectedSubjectMask"
              />
              <v-btn :disabled="$store.getters['Ajax/loading']" @click="add" class="success mt-4">Dodaj</v-btn>
            </v-row>
            <v-row v-if="selectedType !== null && notificationEventTypes.find(v => v.value === selectedType).dates">
              <v-col cols="12" md="6" class="px-3">
                <php-date-diff-chooser :value.sync="selectedNotifyAfter" label="Powiadom po" :can-be-disabled="false" />
              </v-col>
              <v-col cols="12" md="6" class="px-3">
                <php-date-diff-chooser :value.sync="repeatNotifyAfter" label="Powtarzaj co" :can-be-disabled="true" />
              </v-col>
            </v-row>
          </v-form>
          <v-divider />
        </td>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" persistent max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">Lista dostępnych tagów</span>
        </v-card-title>
        <v-card-text>
          <b>{STANDARD}</b> - standardowy temat wiadomości z systemu <br />
          <b>{ID}</b> - unikalny identyfikator zamówienia <br />

          <b>{CREATED_AT}</b> - data złożenia zamówienia <br />
          <b>{CREATED_BY}</b> - Imię i nazwisko osoby zamawiającej <br />

          <b>{PRICE}</b> - w przypadku wybranej oferty suma cen zamawianych usług <br />

          {ATTRIBUTE:[<span style="text-decoration: #0f0f0f line-through">example</span>]} - w przypadku ustawienia dodatkowych parametrów możliwe jest użycie
          także tych parametrów w temacie wiadomości <br />

          <span v-for="parameter in additionalParameters" :key="parameter.id">
            <b>{ATTRIBUTE:[{{ parameter.name }}]}</b> <br />
          </span>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="blue darken-1" text @click.native="dialog = false">{{ $t("global.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { notificationTypeNames } from "../../staticData/NotificationSubscription";
import filterOverAllProperty from "../../repository/filterOverAllProperty";
import isEmail from "validator/lib/isEmail";
import PhpDateDiffChooser from "../../components/CustomInputComponents/PhpDateDiffChooser";

export default {
  name: "AdminMailingView",
  components: { PhpDateDiffChooser },
  data: () => ({
    search: null,
    selectedType: null,
    notificationResponse: [],
    selectedCompany: null,
    selectedEmail: null,
    selectedSubjectMask: null,
    selectedNotifyAfter: null,
    repeatNotifyAfter: null,
    dialog: null,
    additionalParameters: [],
    expanded: []
  }),
  watch: {
    selectedType() {
      this.downloadNotificationSettings();
    }
  },
  created() {
    this.selectedType = this.notificationEventTypes[0].value;
  },
  computed: {
    emailRules() {
      return [
        v => (v != null && v.length > 0) || this.$t("user.validation.emailIsRequired"),
        v => v === null || isEmail(v) || this.$t("user.validation.emailMustBeValid")
      ];
    },
    headers() {
      return [
        { text: "Nazwa", value: "name" },
        { text: "Subskrypcje", sortable: false },
        { text: "", sortable: false }
      ];
    },
    notificationEventTypes: notificationTypeNames
  },
  methods: {
    downloadParamList(companyId) {
      this.axios.get("/admin/notification-subscription-admin/get-additional-parameters", { params: { companyId: companyId } }).then(resp => {
        this.additionalParameters = resp.data;
      });
    },
    openDialogInfo(companyId) {
      this.downloadParamList(companyId);
      this.dialog = true;
    },
    openEditRow(props) {
      //this.expanded.push(item);
      console.log(props);
      props.isExpanded = true;
      props.expand(props.item);
      this.selectedCompany = props.item.id;
      this.selectedEmail = null;
      this.selectedType = null;
      this.selectedNotifyAfter = null;
      this.repeatNotifyAfter = null;
      //this.$nextTick();
    },
    filterOverAllProperty: filterOverAllProperty,
    downloadNotificationSettings() {
      this.axios.get("/admin/notification-subscription-admin/get").then(resp => {
        this.notificationResponse = resp.data;
      });
    },
    add() {
      if (this.$refs.form.validate())
        this.axios
          .post("/admin/notification-subscription-admin/add", {
            email: this.selectedEmail,
            company_id: this.selectedCompany,
            type: this.selectedType,
            subject_mask: this.selectedSubjectMask,
            notify_after: this.selectedNotifyAfter,
            repeat_notify_after: this.repeatNotifyAfter
          })
          .then(() => {
            this.downloadNotificationSettings();
            this.selectedEmail = null;
            this.$store.dispatch("snackbarPush", {
              text: this.$t("global.saved"),
              position: "down"
            });
          });
    },
    remove(itemId) {
      let dialog = this.$store.dispatch("confirmDialog", {
        content: "Od tego momentu na wybrany adres mailowy nie będą wysyłane powiadomienia o wybranym typie zdarzenia.",
        title: "Usunąć subskrypcje?"
      });
      dialog
        .then(() => {
          this.axios
            .post("/admin/notification-subscription-admin/delete", {
              id: itemId
            })
            .then(() => {
              this.downloadNotificationSettings();
              this.$store.dispatch("AUTH_UPDATE_SETTINGS").then(() => {
                this.$store.dispatch("snackbarPush", {
                  text: "Subskrypcja odwołana",
                  position: "down"
                });
              });
            });
        })
        .catch(resp => {
          if (typeof resp !== "undefined") {
            console.log(resp);
          }
        });
    }
  }
};
</script>

<style scoped></style>
